import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../../../shared/api/apiClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserPermissions } from "../../../../../src/shared/lib/auth/authHelper";
import "./LeadsPage.css";

const normalize = (v) =>
  v === null || v === undefined ? "" : String(v).toLowerCase();

const formatDateForFilter = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString().toLowerCase();
  } catch {
    return String(value).toLowerCase();
  }
};

const applySorting = (items, config) => {
  if (!config || !config.key) return items;

  const { key, direction } = config;
  const dir = direction === "asc" ? 1 : -1;

  const getVal = (lead) => {
    if (!lead) return null;

    switch (key) {
      case "ui_id":
      case "budget":
      case "prepayment":
      case "balance":
      case "contact_ui_id":
      case "owner_ui_id":
        return lead[key] != null ? Number(lead[key]) : null;
      case "created_at":
        return lead[key] ? new Date(lead[key]).getTime() : null;
      default:
        return lead[key] ?? null;
    }
  };

  const sorted = [...items].sort((a, b) => {
    const av = getVal(a);
    const bv = getVal(b);

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  return sorted;
};

const LEAD_STATUSES = ["new", "contacted", "in_work", "in_deal", "won", "lost"];

const LEAD_TABS = [
  { key: "active", label: "leads_tab_active" },
  { key: "in_deal", label: "leads_tab_in_deal" },
  { key: "won", label: "leads_tab_won" },
  { key: "lost", label: "leads_tab_lost" },
];

const isLockedLead = (lead) => ["in_deal", "won", "lost"].includes(lead?.status);

const getLeadOrigin = (lead, sourceNameById) => {
  const raw =
    lead?.origin_type ||
    lead?.origin ||
    lead?.source_type ||
    sourceNameById?.[lead?.source_id] ||
    lead?.source_name ||
    "";
  const value = normalize(raw);

  if (value.includes("telegram")) {
    return { key: "telegram", icon: "🤖", label: "lead_origin_telegram_label", title: "lead_origin_telegram" };
  }

  if (value.includes("csv") || value.includes("import")) {
    return { key: "csv", icon: "📥", label: "lead_origin_csv_label", title: "lead_origin_csv" };
  }

  if (value.includes("website") || value.includes("web")) {
    return { key: "website", icon: "🌐", label: "lead_origin_website_label", title: "lead_origin_website" };
  }

  if (value.includes("api")) {
    return { key: "api", icon: "🔗", label: "lead_origin_api_label", title: "lead_origin_api" };
  }

  return null;
};

const defaultColumnFilters = {
  ui_id: "",
  title: "",
  status: "",
  budget: "",
  prepayment: "",
  balance: "",
  contact_ui_id: "",
  source_id: "",
  owner_ui_id: "",
  created_at: "",
};

const getCompactPaginationItems = (currentPage, totalPages) => {
  const total = Number(totalPages || 0);
  const current = Math.min(Math.max(Number(currentPage || 1), 1), Math.max(total, 1));
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
};

const LeadsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const academyTablesActive = searchParams.get("academy_tables") === "1";
  const academyTaskTitle = searchParams.get("academy_task_title") || "";
  const academyTaskDescription =
    searchParams.get("academy_task_description") || "";
  const academyTaskDueAt = searchParams.get("academy_task_due") || "";

  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "ui_id",
    direction: "desc",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);
  const [linkedContactFilter, setLinkedContactFilter] = useState(null);
  const [linkedLeadFilter, setLinkedLeadFilter] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage, setLeadsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("active");
  const [tabCounts, setTabCounts] = useState({ active: 0, in_deal: 0, won: 0, lost: 0 });
  const [tabCache, setTabCache] = useState({ active: null, in_deal: null, won: null, lost: null });

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState(null);
  const [savingFilter, setSavingFilter] = useState(false);

  const [openMenuLeadId, setOpenMenuLeadId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const openMenuRef = useRef(null);

  const [updatingLeadIds, setUpdatingLeadIds] = useState({});

  const fetchLeads = async (tab = activeTab, { force = false } = {}) => {
    if (!force && Array.isArray(tabCache[tab])) {
      setLeads(tabCache[tab]);
      setCurrentPage(1);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/leads/grouped", {
        params: { group: tab, skip: 0, limit: 500 },
      });

      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const counts = res.data?.counts || {};
      setLeads(items);
      setTabCache((prev) => ({ ...prev, [tab]: items }));
      setTabCounts((prev) => ({
        active: Number(counts.active ?? prev.active ?? 0),
        in_deal: Number(counts.in_deal ?? prev.in_deal ?? 0),
        won: Number(counts.won ?? prev.won ?? 0),
        lost: Number(counts.lost ?? prev.lost ?? 0),
      }));
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("error_fetching_leads"));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = async (tab) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
    setSearchQuery("");
    setCurrentPage(1);

    const cachedItems = Array.isArray(tabCache[tab]) ? tabCache[tab] : [];

    if (linkedLeadFilter?.id || linkedLeadFilter?.uiId) {
      const leadId = String(linkedLeadFilter.id || "");
      const leadUiId = String(linkedLeadFilter.uiId || "");

      const exactLeadItems = cachedItems.filter(
        (lead) =>
          (leadId && String(lead?.id ?? "") === leadId) ||
          (leadUiId && String(lead?.ui_id ?? "") === leadUiId)
      );

      setColumnFilters(defaultColumnFilters);
      setLeads(exactLeadItems);
      return;
    }

    if (linkedContactFilter?.id) {
      const contactId = String(linkedContactFilter.id);

      const contactItems = cachedItems.filter(
        (lead) =>
          String(lead?.contact_id ?? "") === contactId ||
          String(lead?.contact_ui_id ?? "") === contactId
      );

      setColumnFilters((prev) => ({
        ...defaultColumnFilters,
        ...prev,
        contact_ui_id: contactId,
      }));
      setLeads(contactItems);
      return;
    }

    setColumnFilters(defaultColumnFilters);

    if (Array.isArray(tabCache[tab])) {
      setLeads(tabCache[tab]);
      return;
    }

    await fetchLeads(tab);
  };

  const fetchSources = async () => {
    try {
      const res = await api.get("/sources", {
        params: { skip: 0, limit: 1000 },
      });
      setSources(res.data || []);
    } catch (e) {
      console.error(e);
      setSources([]);
    }
  };

  const sourceNameById = useMemo(() => {
    const map = {};
    for (const s of sources) map[s.id] = s.name;
    return map;
  }, [sources]);

  const fetchTasks = async () => {
    try {
      const res = await api.get("/tasks", { params: { skip: 0, limit: 1000 } });
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setTasks([]);
    }
  };

  const tasksByLeadId = useMemo(() => {
    const map = new Map();
    for (const task of tasks) {
      if (!task?.lead_id) continue;
      const key = String(task.lead_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(task);
    }
    return map;
  }, [tasks]);

  const openTaskFromLink = (task) => {
    if (!task) return;
    navigate("/menu/sales/tasks", {
      state: {
        open_task_id: task.ui_id || task.id,
        task_search: `#task-${task.ui_id || task.id}`,
      },
    });
  };

  const renderTaskLinks = (lead) => {
    const list = tasksByLeadId.get(String(lead.id)) || tasksByLeadId.get(String(lead.ui_id)) || [];
    if (!list.length) return "-";

    const first = list[0];
    const more = list.length - 1;

    return (
      <div className="leadTaskLinks">
        <button type="button" className="leadTaskLink" onClick={() => openTaskFromLink(first)}>
          ✅ #task-{first.ui_id || first.id}
        </button>
        {more > 0 ? <span className="leadTaskMore">+{more}</span> : null}
      </div>
    );
  };

  const loadSavedFilters = async () => {
    try {
      setFiltersLoading(true);
      setFiltersError(null);

      const res = await api.get("/saved-filters/leads");
      const items = res.data || [];
      setSavedFilters(items);

      const def = items.find((f) => f.is_default);
      if (def && !selectedFilterId) {
        applySavedFilter(def);
      }
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail || t("error_loading_saved_filters")
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  const applySavedFilter = (filter) => {
    if (!filter) return;

    const filtersObj = filter.filters || {};
    const cf = filtersObj.columnFilters || defaultColumnFilters;
    const sq = filtersObj.searchQuery || "";

    setSearchQuery(sq);
    setColumnFilters({
      ...defaultColumnFilters,
      ...cf,
    });

    setSortConfig({
      key: filter.sort_by || "ui_id",
      direction: filter.sort_dir || "desc",
    });

    setLeadsPerPage(filter.per_page || 10);
    setCurrentPage(1);
    setSelectedFilterId(filter.id);
  };

  const handleSelectSavedFilter = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedFilterId(null);
      return;
    }

    const id = Number(val);
    const f = savedFilters.find((x) => x.id === id);
    if (f) applySavedFilter(f);
  };

  const handleSaveCurrentAsNew = async () => {
    const name = window.prompt(t("enter_filter_name")) || "";
    if (!name.trim()) return;

    const makeDefault = window.confirm(t("make_filter_default"));

    const payload = {
      name: name.trim(),
      filters: {
        searchQuery,
        columnFilters,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: leadsPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.post("/saved-filters/leads", payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail || t("error_saving_filter")
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const handleUpdateSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(t("select_filter_first"));
      return;
    }

    const makeDefault = window.confirm(t("make_filter_default"));

    const payload = {
      name: undefined,
      filters: {
        searchQuery,
        columnFilters,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: leadsPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.put(`/saved-filters/${selectedFilterId}`, payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail || t("error_updating_filter")
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(t("select_filter_first"));
      return;
    }

    const confirmed = window.confirm(t("confirm_delete_filter"));
    if (!confirmed) return;

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.delete(`/saved-filters/${selectedFilterId}`);
      setSelectedFilterId(null);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail || t("error_deleting_filter")
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const clearLinkedContactFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("contact_id");
    nextParams.delete("lead_id");
    nextParams.delete("lead_ui_id");
    setSearchParams(nextParams, { replace: true });

    setLinkedContactFilter(null);
    setLinkedLeadFilter(null);
    setSearchQuery("");
    setColumnFilters(defaultColumnFilters);
    setCurrentPage(1);

    const restoredCounts = {
      active: Array.isArray(tabCache.active) ? tabCache.active.length : 0,
      in_deal: Array.isArray(tabCache.in_deal) ? tabCache.in_deal.length : 0,
      won: Array.isArray(tabCache.won) ? tabCache.won.length : 0,
      lost: Array.isArray(tabCache.lost) ? tabCache.lost.length : 0,
    };

    setTabCounts(restoredCounts);
    setLeads(Array.isArray(tabCache[activeTab]) ? tabCache[activeTab] : []);
  };

  const loadContactLeadsFromUrl = async (
    contactId,
    requestedLeadId = "",
    requestedLeadUiId = ""
  ) => {
    const normalizedContactId = String(contactId || "").trim();
    const normalizedLeadId = String(requestedLeadId || "").trim();
    const normalizedLeadUiId = String(requestedLeadUiId || "").trim();

    if (!normalizedContactId && !normalizedLeadId && !normalizedLeadUiId) {
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const groups = LEAD_TABS.map((tab) => tab.key);
      const responses = await Promise.all(
        groups.map((group) =>
          api.get("/leads/grouped", {
            params: { group, skip: 0, limit: 500 },
          })
        )
      );

      const nextCache = {};
      const filteredItemsByGroup = {
        active: [],
        in_deal: [],
        won: [],
        lost: [],
      };
      const filteredCounts = {
        active: 0,
        in_deal: 0,
        won: 0,
        lost: 0,
      };

      let firstMatchedLead = null;
      let firstMatchedTab = "active";

      responses.forEach((response, index) => {
        const group = groups[index];
        const items = Array.isArray(response.data?.items)
          ? response.data.items
          : [];

        nextCache[group] = items;

        const matchingItems = items.filter((lead) => {
          if (normalizedLeadId || normalizedLeadUiId) {
            return (
              (normalizedLeadId &&
                String(lead?.id ?? "") === normalizedLeadId) ||
              (normalizedLeadUiId &&
                String(lead?.ui_id ?? "") === normalizedLeadUiId)
            );
          }

          return (
            String(lead?.contact_id ?? "") === normalizedContactId ||
            String(lead?.contact_ui_id ?? "") === normalizedContactId
          );
        });

        filteredItemsByGroup[group] = matchingItems;
        filteredCounts[group] = matchingItems.length;

        if (!firstMatchedLead && matchingItems.length > 0) {
          firstMatchedLead = matchingItems[0];
          firstMatchedTab = group;
        }
      });

      setTabCache(nextCache);
      setTabCounts(filteredCounts);
      setActiveTab(firstMatchedTab);
      setLeads(filteredItemsByGroup[firstMatchedTab] || []);
      setSearchQuery("");

      if (normalizedLeadId || normalizedLeadUiId) {
        setColumnFilters(defaultColumnFilters);
        setLinkedLeadFilter({
          id: firstMatchedLead?.id || normalizedLeadId || null,
          uiId: firstMatchedLead?.ui_id || normalizedLeadUiId || null,
          label:
            firstMatchedLead?.title ||
            `#${firstMatchedLead?.ui_id || normalizedLeadUiId || normalizedLeadId}`,
        });
      } else {
        setColumnFilters({
          ...defaultColumnFilters,
          contact_ui_id: normalizedContactId,
        });
        setLinkedLeadFilter(null);
      }

      setLinkedContactFilter({
        id:
          firstMatchedLead?.contact_id ||
          normalizedContactId ||
          firstMatchedLead?.contact_ui_id,
        label:
          firstMatchedLead?.contact_display_name ||
          firstMatchedLead?.contact_primary_email ||
          firstMatchedLead?.contact_primary_phone ||
          `#${firstMatchedLead?.contact_ui_id || normalizedContactId}`,
      });

      setCurrentPage(1);
      return true;
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail ||
          t("error_fetching_leads", {
            defaultValue: "Failed to load leads",
          })
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const contactId = searchParams.get("contact_id");
    const leadId = searchParams.get("lead_id");
    const leadUiId = searchParams.get("lead_ui_id");

    if (contactId || leadId || leadUiId) {
      loadContactLeadsFromUrl(contactId, leadId, leadUiId);
    } else {
      fetchLeads("active", { force: true });
    }

    fetchSources();
    fetchTasks();
    loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openMenuLeadId == null) return;

    const onDown = (e) => {
      if (!openMenuRef.current) return;
      if (!openMenuRef.current.contains(e.target)) {
        setOpenMenuLeadId(null);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenuLeadId(null);
    };

    const onScroll = () => {
      setOpenMenuLeadId(null);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [openMenuLeadId]);

  const patchLeadInState = (leadId, patch) => {
    setLeads((prev) =>
      prev.map((item) => (item.id === leadId ? { ...item, ...patch } : item))
    );
  };

  const setLeadUpdating = (leadId, value) => {
    setUpdatingLeadIds((prev) => ({
      ...prev,
      [leadId]: value,
    }));
  };

  const handleUpdateLeadStatus = async (lead, status) => {
    if (!lead || !status || status === lead.status) return;

    const previousStatus = lead.status;
    patchLeadInState(lead.id, { status });
    setLeadUpdating(lead.id, true);
    setError(null);

    try {
      await api.put(`/leads/${lead.id}`, { status });
    } catch (e) {
      console.error(e);
      patchLeadInState(lead.id, { status: previousStatus });
      setError(
        e?.response?.data?.detail ||
          t("error_updating_lead", {
            defaultValue: "Failed to update lead",
          })
      );
    } finally {
      setLeadUpdating(lead.id, false);
    }
  };

  const handleUpdateLeadBudget = async (lead, rawValue) => {
    if (!lead) return;

    const value = String(rawValue ?? "").trim();
    const previousBudget = lead.budget;

    let budget = null;
    if (value !== "") {
      const numeric = Number(value);
      if (Number.isNaN(numeric) || numeric < 0) {
        setError(
          t("invalid_lead_budget", {
            defaultValue: "Budget must be a positive number",
          })
        );
        return;
      }
      budget = numeric;
    }

    const previousNormalized =
      previousBudget === null || previousBudget === undefined
        ? null
        : Number(previousBudget);

    if (previousNormalized === budget) return;

    patchLeadInState(lead.id, { budget });
    setLeadUpdating(lead.id, true);
    setError(null);

    try {
      await api.put(`/leads/${lead.id}`, { budget });
    } catch (e) {
      console.error(e);
      patchLeadInState(lead.id, { budget: previousBudget });
      setError(
        e?.response?.data?.detail ||
          t("error_updating_lead", {
            defaultValue: "Failed to update lead",
          })
      );
    } finally {
      setLeadUpdating(lead.id, false);
    }
  };

  const handleUpdateLeadPrepayment = async (lead, rawValue) => {
    if (!lead) return;

    const value = String(rawValue ?? "").trim();
    const previousPrepayment = lead.prepayment;
    const previousBalance = lead.balance;
    const previousPaymentStatus = lead.payment_status;

    let prepayment = null;
    if (value !== "") {
      const numeric = Number(value);
      if (Number.isNaN(numeric) || numeric < 0) {
        setError(t("invalid_lead_prepayment", { defaultValue: "Prepayment must be a positive number" }));
        return;
      }
      prepayment = numeric;
    }

    const budget = lead.budget === null || lead.budget === undefined || lead.budget === "" ? null : Number(lead.budget);
    const balance = budget === null ? null : Math.max(Number((budget - (prepayment || 0)).toFixed(2)), 0);
    const payment_status = prepayment && budget && prepayment >= budget ? "paid" : prepayment ? "partial" : "unpaid";

    patchLeadInState(lead.id, { prepayment, balance, payment_status });
    setLeadUpdating(lead.id, true);
    setError(null);

    try {
      await api.put(`/leads/${lead.id}`, { prepayment });
    } catch (e) {
      console.error(e);
      patchLeadInState(lead.id, {
        prepayment: previousPrepayment,
        balance: previousBalance,
        payment_status: previousPaymentStatus,
      });
      setError(e?.response?.data?.detail || t("error_updating_lead", { defaultValue: "Failed to update lead" }));
    } finally {
      setLeadUpdating(lead.id, false);
    }
  };

  const renderPaymentSummary = (lead) => {
    const balance = lead?.balance ?? (lead?.budget != null ? Math.max(Number(lead.budget || 0) - Number(lead.prepayment || 0), 0) : null);
    return (
      <div className="leadPaymentSummary" title={t("lead_balance_due", { defaultValue: "Balance due" })}>
        <b>{balance ?? "-"}</b>
        <span>{t(`payment_status_${lead?.payment_status || "unpaid"}`, { defaultValue: lead?.payment_status || "unpaid" })}</span>
      </div>
    );
  };

  const shouldRequireBudgetBeforeConvert = (lead) => {
    const budget = Number(lead?.budget);
    return !Number.isFinite(budget) || budget <= 0;
  };

  const handleConvertToDeal = async (lead) => {
    if (shouldRequireBudgetBeforeConvert(lead)) {
      setError(
        t("lead_budget_required_before_convert", {
          defaultValue:
            "Укажите бюджет больше нуля перед конвертацией лида в сделку.",
        })
      );
      return;
    }

    const confirmed = window.confirm(
      t("confirm_convert_lead_to_deal")
    );
    if (!confirmed) return;

    try {
      setError(null);

      await api.post(`/leads/${lead.id}/convert-to-deal`);

      await fetchLeads(activeTab, { force: true });
      navigate("/menu/sales/deals");
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("error_converting_lead"));
    }
  };

  const handleDeleteLead = async (lead) => {
    const visibleId = lead?.ui_id ?? lead?.id;
    const visibleTitle = lead?.title || t("lead_title", { defaultValue: "Lead" });

    let preview = null;
    try {
      const response = await api.get(`/leads/${lead.id}/delete-preview`);
      preview = response.data || null;
    } catch (previewError) {
      console.error("Failed to load lead delete preview", previewError);
    }

    const message = preview
      ? [
          t("confirm_delete_lead", {
            id: visibleId,
            title: visibleTitle,
            defaultValue: "Удалить лид #{{id}} — {{title}}?",
          }),
          "",
          t("will_be_deleted", { defaultValue: "Будет удалено:" }),
          `• ${t("sales_tasks", { defaultValue: "Задачи" })}: ${Number(preview.tasks || 0)}`,
          `• ${t("sales_deals", { defaultValue: "Сделки" })}: ${Number(preview.deals || 0)}`,
          `• ${t("sales_activities", { defaultValue: "Активности" })}: ${Number(preview.activities || 0)}`,
          `• ${t("files", { defaultValue: "Файлы" })}: ${Number(preview.files || 0)}`,
          "",
          t("delete_cannot_be_undone", {
            defaultValue: "Это действие нельзя отменить.",
          }),
        ].join("\n")
      : t("confirm_delete_lead", {
          id: visibleId,
          title: visibleTitle,
          defaultValue:
            "Удалить лид #{{id}} — {{title}} и всю связанную цепочку?",
        });

    const confirmed = window.confirm(message);
    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);
      setOpenMenuLeadId(null);

      await api.delete(`/leads/${lead.id}`);

      setLeads((prev) => prev.filter((x) => x.id !== lead.id));
      setCurrentPage((prev) => Math.max(1, prev));
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail ||
          t("error_deleting_lead", { defaultValue: "Failed to delete lead" })
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedLeads = useMemo(() => {
    let result = [...leads];

    const global = searchQuery.trim().toLowerCase();

    if (global) {
      result = result.filter((l) => {
        const fields = [
          l.ui_id,
          l.title,
          l.status,
          l.budget,
          l.prepayment,
          l.balance,
          l.payment_status,
          l.contact_id,
          l.contact_ui_id,
          l.contact_display_name,
          l.contact_primary_email,
          l.contact_primary_phone,
          sourceNameById[l.source_id] || l.source_name || "",
          l.owner_ui_id,
          l.owner_display_name,
          l.owner_email,
          formatDateForFilter(l.created_at),
        ];
        return fields.some((f) => normalize(f).includes(global));
      });
    }

    const cf = columnFilters;
    result = result.filter((l) => {
      if (cf.ui_id && !String(l.ui_id ?? "").includes(cf.ui_id.trim()))
        return false;

      if (cf.title && !normalize(l.title).includes(cf.title.trim().toLowerCase()))
        return false;

      if (cf.status && !normalize(l.status).includes(cf.status.trim().toLowerCase()))
        return false;

      if (cf.budget && !String(l.budget ?? "").includes(cf.budget.trim()))
        return false;

      if (cf.prepayment && !String(l.prepayment ?? "").includes(cf.prepayment.trim()))
        return false;

      if (cf.balance && !String(l.balance ?? "").includes(cf.balance.trim()))
        return false;

      if (cf.contact_ui_id) {
        const needle = cf.contact_ui_id.trim().toLowerCase();
        const haystack = [
          l.contact_id,
          l.contact_ui_id,
          l.contact_display_name,
          l.contact_primary_email,
          l.contact_primary_phone,
        ].map((v) => normalize(v)).join(" ");
        if (!haystack.includes(needle)) return false;
      }

      if (
        cf.source_id &&
        !normalize(sourceNameById[l.source_id] || "").includes(
          cf.source_id.trim().toLowerCase()
        )
      )
        return false;

      if (cf.owner_ui_id) {
        const needle = cf.owner_ui_id.trim().toLowerCase();
        const haystack = [
          l.owner_ui_id,
          l.owner_display_name,
          l.owner_email,
        ].map((v) => normalize(v)).join(" ");
        if (!haystack.includes(needle)) return false;
      }

      if (
        cf.created_at &&
        !formatDateForFilter(l.created_at).includes(cf.created_at.trim().toLowerCase())
      )
        return false;

      return true;
    });

    result = applySorting(result, sortConfig);
    return result;
  }, [leads, searchQuery, columnFilters, sortConfig, sourceNameById]);

  const totalLeads = filteredAndSortedLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalLeads / (leadsPerPage || 1)));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * leadsPerPage;
  const visibleLeads = filteredAndSortedLeads.slice(
    startIdx,
    startIdx + leadsPerPage
  );

  const paginate = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleLeadsPerPageChange = (e) => {
    const value = Number(e.target.value);
    if (!value || value <= 0) return;
    setLeadsPerPage(value);
    setCurrentPage(1);
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      let direction = "asc";
      if (prev.key === key && prev.direction === "asc") direction = "desc";
      return { key, direction };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const showingText =
    totalLeads === 0
      ? t("showing_entries", { start: 0, end: 0, total: 0 })
      : t("showing_entries", {
          start: startIdx + 1,
          end: Math.min(startIdx + leadsPerPage, totalLeads),
          total: totalLeads,
        });

  const handleRefreshClick = async () => {
    setSearchQuery("");
    setColumnFilters(defaultColumnFilters);
    setSortConfig({ key: "ui_id", direction: "desc" });
    setLeadsPerPage(10);
    setCurrentPage(1);
    setSelectedFilterId(null);
    setFiltersError(null);
    setOpenMenuLeadId(null);
    const contactId = searchParams.get("contact_id");
    const leadId = searchParams.get("lead_id");
    const leadUiId = searchParams.get("lead_ui_id");

    if (contactId || leadId || leadUiId) {
      await loadContactLeadsFromUrl(contactId, leadId, leadUiId);
    } else {
      await fetchLeads(activeTab, { force: true });
    }
    await fetchSources();
  };

  const openActionsMenu = (leadId, event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();

    const menuWidth = 240;
    const menuHeight = 260;

    const left = Math.max(
      8,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)
    );

    const top =
      rect.bottom + menuHeight > window.innerHeight
        ? Math.max(8, rect.top - menuHeight - 6)
        : rect.bottom + 6;

    setMenuPosition({ top, left });
    setOpenMenuLeadId((prev) => (prev === leadId ? null : leadId));
  };

  const kebabBtnStyle = {
    width: 34,
    height: 24,
    padding: 0,
    fontSize: 16,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const menuStyle = {
    position: "fixed",
    top: menuPosition.top,
    left: menuPosition.left,
    minWidth: 240,
    zIndex: 99999,
    padding: 8,
    borderRadius: 12,
    background: "rgba(25, 25, 30, 0.98)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 18px 42px rgba(0,0,0,0.55)",
  };

  const itemStyle = (disabled) => ({
    width: "100%",
    textAlign: "left",
    padding: "9px 10px",
    borderRadius: 9,
    border: "none",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
  });

  const iconStyle = {
    width: 20,
    height: 20,
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 20px",
  };

  const renderOriginBadge = (lead) => {
    const origin = getLeadOrigin(lead, sourceNameById);
    if (!origin) return null;

    return (
      <span
        className={`leadsOriginBadge leadsOriginBadge--${origin.key}`}
        title={t(origin.title)}
      >
        <span className="leadsOriginBadgeIcon" aria-hidden="true">
          {origin.icon}
        </span>
        <span className="leadsOriginBadgeText">{t(origin.label)}</span>
      </span>
    );
  };

  const renderLeadTitle = (lead, isMobile = false) => {
    const title = lead?.title || t("n_a");
    const origin = getLeadOrigin(lead, sourceNameById);
    const tooltip = origin ? `${title} · ${t(origin.title)}` : title;

    return (
      <div className={isMobile ? "leadsMobileTitleLine" : "leadsTitleCell"} title={tooltip}>
        <span className="leadsTitleText">{title}</span>
        {renderOriginBadge(lead)}
      </div>
    );
  };

  const entityButtonStyle = {
    border: "none",
    background: "transparent",
    color: "#8fb3ff",
    padding: 0,
    fontWeight: 750,
    textAlign: "left",
    cursor: "pointer",
    lineHeight: 1.16,
    display: "block",
    maxWidth: 220,
    minWidth: 0,
  };

  const entityTitleStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const entityMetaStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "rgba(255,255,255,0.52)",
    fontSize: 11,
    marginTop: 2,
    fontWeight: 600,
  };

  const renderContactCell = (lead) => {
    if (!lead?.contact_id) return t("n_a");

    const name =
      lead.contact_display_name ||
      `${t("sales_leads_contact", { defaultValue: "Contact" })} #${
        lead.contact_ui_id || lead.contact_id
      }`;

    const phone = String(lead.contact_primary_phone || "").trim();
    const email = String(lead.contact_primary_email || "").trim();

    return (
      <div className="leadContactCell">
        <button
          type="button"
          className="leadContactLink"
          title={t("open_contact", { defaultValue: "Open contact" })}
          onClick={() =>
            navigate(`/menu/sales/contacts?contact_id=${lead.contact_id}`)
          }
        >
          {name}
        </button>

        {phone ? (
          <a
            href={`tel:${phone.replace(/[^\d+]/g, "")}`}
            className="leadPhoneLink"
            onClick={(event) => event.stopPropagation()}
            title={t("call", {
              defaultValue: `Call ${phone}`,
            })}
            aria-label={t("call", {
              defaultValue: `Call ${phone}`,
            })}
          >
            <span aria-hidden="true">📞</span>
            <span>{phone}</span>
          </a>
        ) : email ? (
          <span className="leadContactMeta" title={email}>
            {email}
          </span>
        ) : (
          <span className="leadContactMeta">
            {t("id")} {lead.contact_ui_id || lead.contact_id}
          </span>
        )}
      </div>
    );
  };

  const renderOwnerCell = (item) => {
    if (!item?.owner_id) return t("n_a");

    const label =
      item.owner_display_name ||
      item.owner_email ||
      `${t("sales_contacts_owner", { defaultValue: "Owner" })} #${item.owner_ui_id || item.owner_id}`;

    const meta = item.owner_email || (item.owner_ui_id ? `ID ${item.owner_ui_id}` : "");

    return (
      <button
        type="button"
        style={entityButtonStyle}
        title={t("open_employee", { defaultValue: "Open employee" })}
        onClick={() => navigate(`/menu/employees?user_id=${item.owner_id}`)}
      >
        <span style={entityTitleStyle}>{label}</span>
        {meta && <span style={entityMetaStyle}>{meta}</span>}
      </button>
    );
  };

  const MobileRelationLine = ({ label, children }) => (
    <span className="crmMobileRelationLine">
      <span className="crmMobileRelationLabel">{label}</span>
      <span className="crmMobileRelationValue">{children}</span>
    </span>
  );

  const renderStatusSelect = (lead, compact = false) => {
    const disabled = !!updatingLeadIds[lead.id] || isLockedLead(lead);

    return (
      <select
        className={compact ? "leadsInlineStatus leadsInlineStatusMobile" : "leadsInlineStatus"}
        value={lead.status || "new"}
        disabled={disabled}
        onChange={(e) => handleUpdateLeadStatus(lead, e.target.value)}
        title={
          isLockedLead(lead)
            ? t("closed_lead_status_locked", {
                defaultValue: "Closed leads cannot be edited here",
              })
            : t("lead_status", { defaultValue: "Lead status" })
        }
      >
        {LEAD_STATUSES.map((status) => (
          <option key={status} value={status}>
            {t(`lead_status_${status}`, { defaultValue: status })}
          </option>
        ))}
      </select>
    );
  };

  const renderBudgetInput = (lead, compact = false) => {
    const disabled = !!updatingLeadIds[lead.id] || isLockedLead(lead);

    return (
      <input
        className={compact ? "leadsInlineBudget leadsInlineBudgetMobile" : "leadsInlineBudget"}
        type="number"
        min="0"
        step="0.01"
        defaultValue={lead.budget ?? ""}
        disabled={disabled}
        placeholder={t("lead_budget", { defaultValue: "Budget" })}
        onBlur={(e) => handleUpdateLeadBudget(lead, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            e.currentTarget.value = lead.budget ?? "";
            e.currentTarget.blur();
          }
        }}
      />
    );
  };

  const renderPrepaymentInput = (lead, compact = false) => {
    const disabled = !!updatingLeadIds[lead.id] || isLockedLead(lead);

    return (
      <input
        className={compact ? "leadsInlineBudget leadsInlineBudgetMobile" : "leadsInlineBudget"}
        type="number"
        min="0"
        step="0.01"
        defaultValue={lead.prepayment ?? ""}
        disabled={disabled}
        placeholder={t("lead_prepayment", { defaultValue: "Prepayment" })}
        onBlur={(e) => handleUpdateLeadPrepayment(lead, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            e.currentTarget.value = lead.prepayment ?? "";
            e.currentTarget.blur();
          }
        }}
      />
    );
  };

  const MenuItem = ({
    title,
    disabled,
    icon,
    iconBg,
    iconBorder,
    academy,
    academyEntityTitle,
    onClick,
    children,
  }) => {
    return (
      <div title={title}>
        <button
          type="button"
          style={itemStyle(disabled)}
          aria-disabled={disabled ? "true" : "false"}
          data-academy={academy}
          data-academy-entity-title={academyEntityTitle || undefined}
          onClick={() => {
            if (disabled) return;
            onClick?.();
          }}
        >
          <span
            style={{
              ...iconStyle,
              background: iconBg,
              border: iconBorder,
            }}
            aria-hidden="true"
          >
            {icon}
          </span>
          <span style={{ flex: 1 }}>{children}</span>
        </button>
      </div>
    );
  };

  return (
    <div className="container mt-4 leadsPage">
      <div data-academy="leads-header" className="leadsPageHeader d-flex justify-content-between align-items-center mb-3 gap-2 flex-nowrap">
        <h2 className="mb-0">{t("sales_leads")}</h2>

        <div className="d-flex gap-2 align-items-center flex-nowrap">
          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshClick}
            disabled={loading}
          >
            {loading
              ? `${t("loading")}...`
              : t("refresh")}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {filtersError && <div className="alert alert-warning py-2">{filtersError}</div>}

      {linkedContactFilter && (
        <div
          className="d-flex align-items-center justify-content-between gap-3 mb-3 px-3 py-2"
          style={{
            width: "fit-content",
            maxWidth: "100%",
            border: "1px solid rgba(91, 140, 255, 0.35)",
            borderRadius: 12,
            background: "rgba(32, 53, 89, 0.46)",
            color: "#dce9ff",
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 750,
            }}
          >
            {linkedLeadFilter ? (
              <>
                {t("lead_title", { defaultValue: "Lead" })}:{" "}
                <strong>{linkedLeadFilter.label}</strong>
                {linkedContactFilter?.label ? (
                  <span style={{ opacity: 0.72 }}>
                    {" "}· {t("sales_leads_contact", { defaultValue: "Contact" })}:{" "}
                    {linkedContactFilter.label}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                {t("sales_leads_contact", { defaultValue: "Contact" })}:{" "}
                <strong>{linkedContactFilter.label}</strong>
              </>
            )}
          </span>

          <button
            type="button"
            className="btn btn-sm btn-outline-light"
            onClick={clearLinkedContactFilter}
            title={t("clear_filter", { defaultValue: "Clear filter" })}
            aria-label={t("clear_filter", { defaultValue: "Clear filter" })}
            style={{
              width: 30,
              height: 30,
              padding: 0,
              flex: "0 0 30px",
              borderRadius: 9,
            }}
          >
            ×
          </button>
        </div>
      )}

      <div data-academy="leads-tabs" className="leadsStatusTabs" role="tablist" aria-label={t("leads_statuses_aria")}>
        {LEAD_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`leadsStatusTab leadsStatusTab--${tab.key} ${activeTab === tab.key ? "is-active" : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <span>{t(tab.label)}</span>
            <span className="leadsStatusTabCount">{tabCounts[tab.key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="table-responsive leadsDesktopTable">
        <div data-academy="leads-toolbar" className="leadsDesktopToolbar d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 px-2 pt-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={leadsPerPage}
              onChange={handleLeadsPerPageChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>

            <div className="text-muted">{showingText}</div>
          </div>

          <div className="input-group input-group-sm" style={{ width: "auto", minWidth: 260 }}>
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading
                  ? `${t("loading")}...`
                  : t("saved_filters")}
              </option>
              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <table data-academy="leads-table" className="table table-striped table-hover align-middle">
          <thead>
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("ui_id")}>
                {t("id")}
                {renderSortArrow("ui_id")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("title")}>
                {t("lead_title")}
                {renderSortArrow("title")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("status")}>
                {t("lead_status")}
                {renderSortArrow("status")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("budget")}>
                {t("lead_budget")}
                {renderSortArrow("budget")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("prepayment")}>
                {t("lead_prepayment", { defaultValue: "Prepayment" })}
                {renderSortArrow("prepayment")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("balance")}>
                {t("lead_balance_due", { defaultValue: "Balance due" })}
                {renderSortArrow("balance")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("contact_ui_id")}>
                {t("sales_leads_contact")}
                {renderSortArrow("contact_ui_id")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("source_id")}>
                {t("lead_source")}
                {renderSortArrow("source_id")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("owner_ui_id")}>
                {t("sales_contacts_owner")}
                {renderSortArrow("owner_ui_id")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("created_at")}>
                {t("created_at")}
                {renderSortArrow("created_at")}
              </th>
              <th>{t("tasks", { defaultValue: "Tasks" })}</th>
              <th className="leadsStickyActions">{t("actions")}</th>
            </tr>

            <tr>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("id")}
                  value={columnFilters.ui_id}
                  onChange={(e) => handleColumnFilterChange("ui_id", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("lead_title")}
                  value={columnFilters.title}
                  onChange={(e) => handleColumnFilterChange("title", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("lead_status")}
                  value={columnFilters.status}
                  onChange={(e) => handleColumnFilterChange("status", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("lead_budget")}
                  value={columnFilters.budget}
                  onChange={(e) => handleColumnFilterChange("budget", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("lead_prepayment", { defaultValue: "Prepayment" })}
                  value={columnFilters.prepayment}
                  onChange={(e) => handleColumnFilterChange("prepayment", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("lead_balance_due", { defaultValue: "Balance due" })}
                  value={columnFilters.balance}
                  onChange={(e) => handleColumnFilterChange("balance", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_leads_contact")}
                  value={columnFilters.contact_ui_id}
                  onChange={(e) => handleColumnFilterChange("contact_ui_id", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("lead_source")}
                  value={columnFilters.source_id}
                  onChange={(e) => handleColumnFilterChange("source_id", t(e.target.value))}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_contacts_owner")}
                  value={columnFilters.owner_ui_id}
                  onChange={(e) => handleColumnFilterChange("owner_ui_id", e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("created_at")}
                  value={columnFilters.created_at}
                  onChange={(e) => handleColumnFilterChange("created_at", e.target.value)}
                />
              </th>
              <th />
              <th className="leadsStickyActions" />
            </tr>
          </thead>

          <tbody>
            {visibleLeads.length === 0 && !loading && (
              <tr>
                <td colSpan={12} className="text-center py-4">
                  {t("sales_leads_empty")}
                </td>
              </tr>
            )}

            {visibleLeads.map((lead) => {
              const isMenuOpen = openMenuLeadId === lead.id;

              return (
                <tr
                  key={lead.id}
                  data-academy="lead-row"
                  data-academy-entity-title={lead.title || ""}
                >
                <td>{lead.ui_id}</td>
                <td>{renderLeadTitle(lead)}</td>
                <td>{renderStatusSelect(lead)}</td>
                <td>{renderBudgetInput(lead)}</td>
                <td>{renderPrepaymentInput(lead)}</td>
                <td>{renderPaymentSummary(lead)}</td>
                <td>{renderContactCell(lead)}</td>
                <td>
                  {sourceNameById[lead.source_id]
                    ? t(sourceNameById[lead.source_id])
                    : t("n_a")}
                </td>
                <td>{renderOwnerCell(lead)}</td>
                <td>
                  {lead.created_at ? new Date(lead.created_at).toLocaleString() : t("n_a")}
                </td>
                <td>{renderTaskLinks(lead)}</td>
                <td className="leadsStickyActions">
                  <span title={t("actions")}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      style={kebabBtnStyle}
                      data-academy="lead-actions-trigger"
                      data-academy-entity-title={lead.title || ""}
                      onClick={(e) => openActionsMenu(lead.id, e)}
                      aria-expanded={isMenuOpen ? "true" : "false"}
                      aria-haspopup="menu"
                    >
                      ⋯
                    </button>
                  </span>

                  {isMenuOpen && createPortal(
                    <div
                      ref={openMenuRef}
                      role="menu"
                      aria-label={t("leads_actions_aria")}
                      data-academy="lead-actions-menu"
                      data-academy-entity-title={lead.title || ""}
                      style={menuStyle}
                    >
                      {getUserPermissions()?.includes("leads.convert") && (
                        <MenuItem
                          title={t("convert_to_deal")}
                          disabled={isLockedLead(lead)}
                          icon="↗"
                          iconBg="rgba(245, 179, 64, 0.22)"
                          iconBorder="1px solid rgba(245, 179, 64, 0.38)"
                          onClick={() => {
                            setOpenMenuLeadId(null);
                            handleConvertToDeal(lead);
                          }}
                        >
                          {t("convert_to_deal")}
                        </MenuItem>
                      )}

                      {getUserPermissions()?.includes("tasks.create") && (
                        <MenuItem
                          title={t("create_task")}
                          disabled={false}
                          icon="+"
                          iconBg="rgba(108,117,125,0.20)"
                          iconBorder="1px solid rgba(108,117,125,0.35)"
                          academy="lead-create-task"
                          academyEntityTitle={lead.title}
                          onClick={() => {
                            setOpenMenuLeadId(null);
                            navigate("/menu/sales/tasks", {
                              state: {
                                lead_id: lead.id,
                                lead_ui_id: lead.ui_id,
                                contact_ui_ids: lead.contact_ui_id ? [lead.contact_ui_id] : [],
                                contact_ids: lead.contact_id ? [lead.contact_id] : [],
                                default_title:
                                  academyTaskTitle || lead.title,
                                default_description:
                                  academyTablesActive
                                    ? academyTaskDescription
                                    : "",
                                default_due_at:
                                  academyTablesActive
                                    ? academyTaskDueAt
                                    : "",
                              },
                            });
                          }}
                        >
                          {t("create_task")}
                        </MenuItem>
                      )}

                      {getUserPermissions()?.includes("leads.delete") && (
                        <>
                          <div
                            style={{
                              height: 1,
                              margin: "6px 2px",
                              background: "rgba(255,255,255,0.10)",
                            }}
                          />

                          <MenuItem
                            title={t("delete")}
                            disabled={loading}
                            icon={<i className="lni lni-trash-3"></i>}
                            iconBg="rgba(220,53,69,0.20)"
                            iconBorder="1px solid rgba(220,53,69,0.35)"
                            onClick={() => handleDeleteLead(lead)}
                          >
                            {t("delete")}
                          </MenuItem>
                        </>
                      )}
                    </div>,
                    document.body
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="leadsMobileTableCard">
        <div className="leadsMobileTableToolbar">
          <div className="leadsMobileToolbarTitle">
            {t("table_filters", { defaultValue: "Table filters" })}
          </div>

          <div className="leadsMobileToolbarActions">
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading
                  ? `${t("loading")}...`
                  : t("saved_filters")}
              </option>
              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button className="btn btn-outline-success" type="button" title={t("save_filter")} onClick={handleSaveCurrentAsNew} disabled={savingFilter}>
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button className="btn btn-outline-primary" type="button" title={t("update_filter")} onClick={handleUpdateSelectedFilter} disabled={savingFilter || !selectedFilterId}>
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button className="btn btn-outline-danger" type="button" title={t("delete_filter")} onClick={handleDeleteSelectedFilter} disabled={savingFilter || !selectedFilterId}>
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <div className="leadsMobileTableMeta">
          <select className="form-select" value={leadsPerPage} onChange={handleLeadsPerPageChange}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <div>{showingText}</div>
        </div>

        <div className="leadsMobileTableSearch">
          <input
            type="text"
            className="form-control"
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="leadsMobileTableScroll">
          <table className="leadsMobileTable">
            <thead>
              <tr>
                <th>{t("id")}</th>
                <th>{t("lead_title")}</th>
              </tr>
              <tr>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("id")}
                    value={columnFilters.ui_id}
                    onChange={(e) => handleColumnFilterChange("ui_id", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("lead_title")}
                    value={columnFilters.title}
                    onChange={(e) => handleColumnFilterChange("title", e.target.value)}
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleLeads.length === 0 && !loading && (
                <tr>
                  <td colSpan={2} className="leadsMobileEmptyCell">
                    {t("sales_leads_empty")}
                  </td>
                </tr>
              )}

              {visibleLeads.map((lead) => (
                <tr
                  key={lead.id}
                  data-academy="lead-row"
                  data-academy-entity-title={lead.title || ""}
                >
                  <td className="leadsMobileIdCell">{lead.ui_id}</td>
                  <td>
                    <div className="leadsMobileLeadCell">
                      <div className="leadsMobileLeadTop">
                        <div className="leadsMobileTitleStack">
                          {renderLeadTitle(lead, true)}
                        </div>
                        {renderStatusSelect(lead, true)}
                      </div>

                      <div className="leadsMobileLeadMeta">
                        <span className="leadsMobileBudgetLine">
                          {t("lead_budget")}: {renderBudgetInput(lead, true)} / {t("lead_prepayment", { defaultValue: "Prepayment" })}: {renderPrepaymentInput(lead, true)}
                        </span>
                        <MobileRelationLine label={t("sales_leads_contact")}>
                          {renderContactCell(lead)}
                        </MobileRelationLine>
                        <span>
                          {t("lead_source")}:{" "}
                          <b>
                            {sourceNameById[lead.source_id]
                              ? t(sourceNameById[lead.source_id])
                              : t("n_a")}
                          </b>
                        </span>
                        <MobileRelationLine label={t("sales_contacts_owner")}>
                          {renderOwnerCell(lead)}
                        </MobileRelationLine>
                      </div>

                      <div className="leadsMobileLeadDate">
                        {lead.created_at ? new Date(lead.created_at).toLocaleString() : t("n_a")}
                      </div>

                      <div className="leadsMobileLeadActions">
                        {getUserPermissions()?.includes("leads.convert") && (
                          <button
                            className="btn btn-warning btn-sm"
                            type="button"
                            onClick={() => handleConvertToDeal(lead)}
                            disabled={isLockedLead(lead)}
                          >
                            {t("convert_to_deal")}
                          </button>
                        )}

                        {getUserPermissions()?.includes("tasks.create") && (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            type="button"
                            data-academy="lead-create-task"
                            data-academy-entity-title={lead.title || ""}
                            onClick={() =>
                              navigate("/menu/sales/tasks", {
                                state: {
                                  lead_id: lead.id,
                                  lead_ui_id: lead.ui_id,
                                  contact_ui_ids: lead.contact_ui_id ? [lead.contact_ui_id] : [],
                                  contact_ids: lead.contact_id ? [lead.contact_id] : [],
                                  default_title:
                                    academyTaskTitle || lead.title,
                                  default_description:
                                    academyTablesActive
                                      ? academyTaskDescription
                                      : "",
                                  default_due_at:
                                    academyTablesActive
                                      ? academyTaskDueAt
                                      : "",
                                },
                              })
                            }
                          >
                            {t("create_task")}
                          </button>
                        )}

                        {getUserPermissions()?.includes("leads.delete") && (
                          <button
                            className="btn btn-outline-danger btn-sm leadsDeleteBtn"
                            type="button"
                            onClick={() => handleDeleteLead(lead)}
                            disabled={loading}
                          >
                            <i className="lni lni-trash-3"></i>
                            <span>{t("delete")}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2 leadsPaginationWrap">
        <nav aria-label={t("page_navigation")}>
          <ul className="pagination justify-content-end mb-0 leadsPagination leadsCompactPagination">
            <li className={`page-item ${safePage === 1 || totalLeads === 0 ? "disabled" : ""}`}><button className="page-link" title={t("first", { defaultValue: "First" })} aria-label={t("first", { defaultValue: "First" })} onClick={() => paginate(1)}>«</button></li>
            <li className={`page-item ${safePage === 1 || totalLeads === 0 ? "disabled" : ""}`}>
              <button
                className="page-link"
                title={t("previous")}
                aria-label={t("previous")}
                onClick={() => paginate(safePage - 1)}
              >
                {t("previous")}
              </button>
            </li>

            {getCompactPaginationItems(safePage, totalPages).map((item) => {
              if (typeof item === "string") {
                return (
                  <li key={item} className="page-item disabled leadsPaginationEllipsis">
                    <span className="page-link">…</span>
                  </li>
                );
              }
              return (
                <li key={item} className={`page-item ${safePage === item ? "active" : ""}`}>
                  <button className="page-link" onClick={() => paginate(item)}>
                    {item}
                  </button>
                </li>
              );
            })}

            <li className={`page-item ${safePage === totalPages || totalLeads === 0 ? "disabled" : ""}`}>
              <button
                className="page-link"
                title={t("next")}
                aria-label={t("next")}
                onClick={() => paginate(safePage + 1)}
              >
                {t("next")}
              </button>
            </li>
            <li className={`page-item ${safePage === totalPages || totalLeads === 0 ? "disabled" : ""}`}><button className="page-link" title={t("last", { defaultValue: "Last" })} aria-label={t("last", { defaultValue: "Last" })} onClick={() => paginate(totalPages)}>»</button></li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default LeadsPage;
